import { useMemo } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Cell, PieChart, Pie, Legend,
} from "recharts";
import { useDateRange } from "@shared/hooks/useDateRange";
import { DateRangeFilter } from "@shared/ui/DateRangeFilter";
import { useEvaluations } from "@features/evaluation/queries";
import { useScheduling } from "@features/patients/queries";
import { computeKpis } from "@features/dashboard/kpis";
import { HttpError } from "@shared/http/client";
import type { EvaluationEnvelope } from "@/types/evaluation";

type Freq = { key: string; count: number };

function topN(items: EvaluationEnvelope[], keyFn: (e: EvaluationEnvelope) => string, n = 10): Freq[] {
  const map = new Map<string, number>();
  for (const e of items) {
    const k = keyFn(e).trim() || "(sin dato)";
    map.set(k, (map.get(k) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
}

function seriesByDay(items: EvaluationEnvelope[]): Freq[] {
  const map = new Map<string, number>();
  for (const e of items) {
    const day = String(e.fechaRegistro || e.fecha || "").slice(0, 10);
    if (!day) continue;
    map.set(day, (map.get(day) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

function distribucionSemaforo(items: EvaluationEnvelope[]) {
  const norm = (v: unknown) => String(v || "").toUpperCase();
  let aptos = 0, gestion = 0, reprog = 0, noApto = 0, otros = 0;
  for (const e of items) {
    const a = norm(e.accionFinal);
    if (a.includes("APTO") && !a.includes("NO")) aptos++;
    else if (a.includes("EN GESTI")) gestion++;
    else if (a.includes("REPROGRAM")) reprog++;
    else if (a.includes("NO APTO") || a.includes("NO SUBSAN")) noApto++;
    else otros++;
  }
  return [
    { name: "Aptos",        value: aptos,   color: "#2E8B57" },
    { name: "En gestión",   value: gestion, color: "#C68415" },
    { name: "Reprogramados",value: reprog,  color: "#B85C1B" },
    { name: "No aptos",     value: noApto,  color: "#B32424" },
    { name: "Sin decisión", value: otros,   color: "#6B7C93" },
  ].filter((d) => d.value > 0);
}

const BAR_COLORS = ["#2E6DB4", "#4A90D9", "#6BAAE6", "#8CC1F0", "#A8D2F5", "#B8DDF9", "#C4E4FA", "#D0EAFB", "#DAF0FD", "#E4F5FE"];

export function IndicadoresPage() {
  const { range } = useDateRange();
  const { data, isLoading, error } = useEvaluations(range);
  const { data: pendData } = useScheduling({ page: 1, limit: 1 });
  const list = data?.items ?? [];
  const summary = useMemo(() => {
    const k = computeKpis(list);
    return {
      total: k.total,
      aptos: k.aptos,
      reprogClinica: list.filter((e) => String(e.accionFinal || "") === "Reprogramar por causa clínica").length,
      reprogAdmin:   list.filter((e) => String(e.accionFinal || "") === "Reprogramar por causa administrativa").length,
      pendientes: pendData?.total ?? 0,
    };
  }, [list, pendData]);

  const daily        = useMemo(() => seriesByDay(list), [list]);
  const byEvaluator  = useMemo(() => topN(list, (e) => String(e.evaluadorNombre || e.evaluador || "")), [list]);
  const byCirugia    = useMemo(() => topN(list, (e) => String(e.cirugia || "")), [list]);
  const byCie10      = useMemo(() => topN(list, (e) => `${e.cie10 || "?"} · ${String(e.diagnostico || "").slice(0, 30)}`), [list]);
  const byProgramador = useMemo(() => topN(list, (e) => String(e.programador || ""), 5), [list]);
  const semaforo      = useMemo(() => distribucionSemaforo(list), [list]);

  return (
    <div className="p-24">
      <div className="page-header">
        <h1>Indicadores</h1>
        <p>Métricas del rango seleccionado y gráficos comparativos por evaluador, cirugía, diagnóstico y programador.</p>
      </div>

      <div className="mb-16"><DateRangeFilter /></div>

      {error instanceof HttpError && (
        <div className="alert-inline alert-inline--danger mt-16">Error: {error.message}</div>
      )}

      {isLoading && <div className="text-muted mt-16">Cargando…</div>}

      <div className="card mt-16">
        <div className="card-header">
          <div className="card-title">📊 Resumen del rango</div>
        </div>
        <div className="indicator-rows">
          <div className="indicator-row"><span>Total valoraciones</span>              <b>{summary.total}</b></div>
          <div className="indicator-row"><span>Aptos para cirugía</span>              <b className="text-success">{summary.aptos}</b></div>
          <div className="indicator-row"><span>Reprogramación clínica</span>          <b className="text-danger">{summary.reprogClinica}</b></div>
          <div className="indicator-row"><span>Reprogramación administrativa</span>   <b className="text-warn">{summary.reprogAdmin}</b></div>
          <div className="indicator-row"><span>Pacientes pendientes (total)</span>    <b>{summary.pendientes}</b></div>
        </div>
      </div>

      <div className="indicators-grid mt-24">
        <div className="wizard-section chart-card chart-card--wide">
          <h4 className="wizard-section-title">Valoraciones por día ({daily.length})</h4>
          {daily.length === 0 ? <div className="text-muted">Sin datos</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={daily} margin={{ top: 8, right: 20, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4EBF4" />
                <XAxis dataKey="key" fontSize={11} tick={{ fill: "#6B7C93" }} />
                <YAxis fontSize={11} tick={{ fill: "#6B7C93" }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Line type="monotone" dataKey="count" stroke="#2E6DB4" strokeWidth={2}
                      dot={{ fill: "#2E6DB4", r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="wizard-section chart-card">
          <h4 className="wizard-section-title">Distribución por decisión</h4>
          {semaforo.length === 0 ? <div className="text-muted">Sin datos</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={semaforo} dataKey="value" nameKey="name" outerRadius={70} label={(entry) => `${entry.value}`}>
                  {semaforo.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="wizard-section chart-card">
          <h4 className="wizard-section-title">Top 10 evaluadores</h4>
          <BarSection items={byEvaluator} />
        </div>

        <div className="wizard-section chart-card">
          <h4 className="wizard-section-title">Top 10 tipos de cirugía</h4>
          <BarSection items={byCirugia} />
        </div>

        <div className="wizard-section chart-card">
          <h4 className="wizard-section-title">Top 10 diagnósticos CIE-10</h4>
          <BarSection items={byCie10} />
        </div>

        <div className="wizard-section chart-card">
          <h4 className="wizard-section-title">Top 5 programadores</h4>
          <BarSection items={byProgramador} />
        </div>
      </div>
    </div>
  );
}

function BarSection({ items }: { items: Freq[] }) {
  if (items.length === 0) return <div className="text-muted">Sin datos</div>;
  const height = Math.max(180, items.length * 28);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={items} layout="vertical" margin={{ top: 4, right: 30, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E4EBF4" horizontal={false} />
        <XAxis type="number" fontSize={11} tick={{ fill: "#6B7C93" }} allowDecimals={false} />
        <YAxis type="category" dataKey="key" fontSize={11} tick={{ fill: "#1A2B45" }} width={160}
               tickFormatter={(v: string) => v.length > 24 ? v.slice(0, 22) + "…" : v} />
        <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [`${v} valoraciones`, "Total"]} />
        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
          {items.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
